// src/ShiftRulesPage.js — v3 Layout a piena pagina

import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from './UserContext';
import { db } from './firebase';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import { logAction } from './loggingService';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import 'moment/locale/it';
import 'react-toastify/dist/ReactToastify.css';
import { Calendar, Save, Trash2, Plus, Users, LayoutGrid, ChevronDown } from 'lucide-react';
import './ShiftRulesPage.css';

// Helper per Data di ancoraggio
// const getNextMonday = () => {
//    const d = new Date();
//    d.setDate(d.getDate() + ((1 + 7 - d.getDay()) % 7 || 7));
//    return moment(d).format('YYYY-MM-DD');
// };

const WEEK_DAYS = [
    { id: 1, label: 'Lunedì' },
    { id: 2, label: 'Martedì' },
    { id: 3, label: 'Mercoledì' },
    { id: 4, label: 'Giovedì' },
    { id: 5, label: 'Venerdì' },
    { id: 6, label: 'Sabato',   isWeekend: true },
    { id: 7, label: 'Domenica', isWeekend: true },
];

export default function ShiftRulesPage() {
    const { userProfile } = useUser();
    const [isLoading, setIsLoading]     = useState(true);
    const [isSaving, setIsSaving]       = useState(false);
    const [staffList, setStaffList]     = useState([]);
    const [activeTab, setActiveTab]     = useState('rotation');
    const [templates, setTemplates]     = useState([]);
    const [editingTemplate, setEditingTemplate] = useState(null);

    useEffect(() => {
        if (!userProfile?.centerId) return;
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const staffSnap = await getDocs(collection(db, 'centers', userProfile.centerId, 'staff'));
                setStaffList(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                const collName = activeTab === 'backoffice' ? 'backofficeTemplates' : 'shiftTemplates';
                const tplSnap  = await getDocs(collection(db, 'centers', userProfile.centerId, collName));
                const loaded   = tplSnap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(t => t.roleType === activeTab || !t.roleType);
                setTemplates(loaded);
                setEditingTemplate(null);
            } catch (err) {
                console.error(err);
                toast.error('Errore nel caricamento dei dati.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [userProfile?.centerId, activeTab]);

    const filteredStaff = useMemo(() => {
        return staffList
            .filter(p => {
                if (p.isDoctorProfile === true || p.role === 'Medico') return false;
                // 🆕 Chi ha il flag "Includi nel motore di calcolo turni" disattivato
                // non deve comparire nemmeno qui, tra le righe dei modelli di
                // turnazione — stessa regola già applicata in ShiftsPage.js.
                if (p.includeInShifts === false) return false;
                return activeTab === 'backoffice'
                    ? p.assignmentType === 'backoffice'
                    : p.assignmentType !== 'backoffice';
            })
            .sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
    }, [staffList, activeTab]);

    const handleCreateTemplate = () => {
        setEditingTemplate({
            id: uuidv4(),
            name: `Nuovo Modello ${activeTab === 'backoffice' ? 'BackOffice' : 'Turnisti'}`,
            roleType: activeTab,
            cycleWeeks: 1,
            //anchorDate: getNextMonday(),
            assignments: [],
        });
    };

    const handleSelectTemplate = (id) => {
        if (!id) { setEditingTemplate(null); return; }
        const tpl = templates.find(t => t.id === id);
        if (tpl) setEditingTemplate(JSON.parse(JSON.stringify(tpl)));
    };

    const handleSaveTemplate = async () => {
        if (!editingTemplate.name.trim())   return toast.error('Inserisci un nome per il modello.');
        //if (!editingTemplate.anchorDate)    return toast.error('Seleziona una data di Ancoraggio valida (Lunedì).');
        setIsSaving(true);
        try {
            const collName = activeTab === 'backoffice' ? 'backofficeTemplates' : 'shiftTemplates';
            const docRef   = doc(db, 'centers', userProfile.centerId, collName, editingTemplate.id);
            await setDoc(docRef, { ...editingTemplate, roleType: activeTab, updatedAt: new Date() });
            setTemplates(prev => {
                const exists = prev.find(t => t.id === editingTemplate.id);
                return exists
                    ? prev.map(t => t.id === editingTemplate.id ? editingTemplate : t)
                    : [...prev, editingTemplate];
            });
            await logAction(userProfile, `ha salvato il modello di turnazione: ${editingTemplate.name}`);
            toast.success('Modello salvato con successo!');
        } catch (err) {
            console.error(err);
            toast.error('Errore durante il salvataggio.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteTemplate = async () => {
        if (!editingTemplate) return;
        if (!window.confirm(`Eliminare il modello "${editingTemplate.name}"?`)) return;
        try {
            const collName = activeTab === 'backoffice' ? 'backofficeTemplates' : 'shiftTemplates';
            await deleteDoc(doc(db, 'centers', userProfile.centerId, collName, editingTemplate.id));
            setTemplates(prev => prev.filter(t => t.id !== editingTemplate.id));
            setEditingTemplate(null);
            toast.success('Modello eliminato.');
        } catch (err) {
            console.error(err);
            toast.error("Errore durante l'eliminazione.");
        }
    };

    const getShift = (week, dayOfWeek, staffId) => {
        if (!editingTemplate) return '';
        const found = editingTemplate.assignments.find(
            a => a.week === week && a.dayOfWeek === dayOfWeek && a.staffId === staffId
        );
        return found ? found.shiftName : '';
    };

    const handleShiftChange = (week, dayOfWeek, staffId, newShift) => {
        const filtered = editingTemplate.assignments.filter(
            a => !(a.week === week && a.dayOfWeek === dayOfWeek && a.staffId === staffId)
        );
        if (newShift) filtered.push({ week, dayOfWeek, staffId, shiftName: newShift });
        setEditingTemplate({ ...editingTemplate, assignments: filtered });
    };

    const isBackoffice = activeTab === 'backoffice';

    if (isLoading) {
        return (
            <div className="srp-root">
                <div className="srp-loading">
                    <div className="srp-spinner" />
                    Caricamento interfaccia…
                </div>
            </div>
        );
    }

    return (
        <div className="srp-root">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

            {/* ── BARRA SUPERIORE ── */}
            <div className="srp-topbar">
                {/* Titolo + tab */}
                <div className="srp-topbar-left">
                    <h1 className="srp-page-title">Modelli di Turnazione</h1>
                    <div className="srp-tabs">
                        <button
                            className={`srp-tab-btn tab-rotation${activeTab === 'rotation' ? ' active' : ''}`}
                            onClick={() => setActiveTab('rotation')}
                        >
                            <Users size={14} /> Turnisti
                        </button>
                        <button
                            className={`srp-tab-btn tab-backoffice${activeTab === 'backoffice' ? ' active' : ''}`}
                            onClick={() => setActiveTab('backoffice')}
                        >
                            <LayoutGrid size={14} /> BackOffice
                        </button>
                    </div>
                </div>

                {/* Selezione template + azioni */}
                <div className="srp-topbar-right">
                    <div className="srp-select-wrapper">
                        <ChevronDown size={14} className="srp-select-icon" />
                        <select
                            className="srp-template-select"
                            value={editingTemplate?.id || ''}
                            onChange={e => handleSelectTemplate(e.target.value)}
                        >
                            <option value="">
                                {templates.length === 0 ? '— Nessun modello salvato —' : '— Seleziona un modello —'}
                            </option>
                            {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>

                    {editingTemplate && !templates.find(t => t.id === editingTemplate.id) === false && (
                        <button
                            className="srp-btn-icon-danger"
                            onClick={handleDeleteTemplate}
                            title="Elimina modello selezionato"
                        >
                            <Trash2 size={15} />
                        </button>
                    )}

                    <button
                        className={`srp-btn-new btn-${isBackoffice ? 'backoffice' : 'rotation'}`}
                        onClick={handleCreateTemplate}
                    >
                        <Plus size={14} /> Nuovo
                    </button>
                </div>
            </div>

            {/* ── CONTENUTO ── */}
            <div className="srp-content">
                {!editingTemplate ? (
                    <div className="srp-empty-editor">
                        <div className="srp-empty-icon"><Calendar size={28} /></div>
                        <h2>Nessun modello selezionato</h2>
                        <p>Seleziona un modello dal menu in alto, oppure creane uno nuovo con il pulsante <strong>+ Nuovo</strong>.</p>
                    </div>
                ) : (
                    <>
                        {/* ── TOOLBAR TEMPLATE ── */}
                        <div className="srp-editor-toolbar">
                            <div className="srp-field srp-field-name">
                                <label>Nome del modello</label>
                                <input
                                    type="text"
                                    value={editingTemplate.name}
                                    onChange={e => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                                    placeholder="Es. Turni Standard Estate…"
                                />
                            </div>
                            <div className="srp-field srp-field-cycle">
                                <label>Durata ciclo</label>
                                <select
                                    value={editingTemplate.cycleWeeks}
                                    onChange={e => {
                                        const n = parseInt(e.target.value);
                                        const filtered = editingTemplate.assignments.filter(a => a.week <= n);
                                        setEditingTemplate({ ...editingTemplate, cycleWeeks: n, assignments: filtered });
                                    }}
                                >
                                    <option value={1}>1 Settimana</option>
                                    <option value={2}>2 Settimane</option>
                                    <option value={3}>3 Settimane</option>
                                    <option value={4}>4 Settimane (Mese)</option>
                                    <option value={5}>5 Settimane</option>
                                    <option value={6}>6 Settimane</option>
                                </select>
                            </div>
                            
                            <button
                                className={`srp-btn-save save-${isBackoffice ? 'backoffice' : 'rotation'}`}
                                onClick={handleSaveTemplate}
                                disabled={isSaving}
                            >
                                <Save size={15} />
                                {isSaving ? 'Salvataggio…' : 'Salva modello'}
                            </button>
                        </div>

                        {/* ── GRIGLIE SETTIMANE ── */}
                        <div className="srp-weeks-area">
                            {Array.from({ length: editingTemplate.cycleWeeks }).map((_, wIndex) => {
                                const weekNumber = wIndex + 1;
                                return (
                                    <div key={weekNumber} className="srp-week-block">
                                        <div className="srp-week-header">
                                            <span className={`srp-week-badge badge-${isBackoffice ? 'backoffice' : 'rotation'}`}>
                                                {weekNumber}
                                            </span>
                                            Settimana {weekNumber}
                                            {editingTemplate.cycleWeeks > 1 && (
                                                <span className="srp-week-counter">
                                                    {editingTemplate.assignments.filter(a => a.week === weekNumber).length} turni assegnati
                                                </span>
                                            )}
                                        </div>
                                        <div className="srp-table-wrapper">
                                            <table className="srp-schedule-table">
                                                <thead>
                                                    <tr>
                                                        <th>Personale</th>
                                                        {WEEK_DAYS.map(day => (
                                                            <th key={day.id} className={day.isWeekend ? 'col-weekend' : ''}>
                                                                {day.label}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredStaff.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={8}>
                                                                <div className="srp-empty-staff">
                                                                    Nessun membro dello staff trovato per questa categoria.
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        filteredStaff.map(staff => (
                                                            <tr key={staff.id}>
                                                                <td className="srp-cell-staff">
                                                                    <div className="srp-staff-name">{staff.name}</div>
                                                                    <div className="srp-staff-role">{staff.role}</div>
                                                                </td>
                                                                {WEEK_DAYS.map(day => {
                                                                    const available = staff.timeSlots || [];
                                                                    const currentVal = getShift(weekNumber, day.id, staff.id);
                                                                    const selectClass = currentVal
                                                                        ? `srp-shift-select ${isBackoffice ? 'has-value-backoffice' : 'has-value'}`
                                                                        : 'srp-shift-select';
                                                                    return (
                                                                        <td key={day.id} className={`srp-cell-shift${day.isWeekend ? ' col-weekend' : ''}`}>
                                                                            <select
                                                                                className={selectClass}
                                                                                value={currentVal}
                                                                                onChange={e => handleShiftChange(weekNumber, day.id, staff.id, e.target.value)}
                                                                            >
                                                                                <option value="">— Libero —</option>
                                                                                {available.map((ts, idx) => (
                                                                                    <option key={idx} value={ts.name}>{ts.name}</option>
                                                                                ))}
                                                                            </select>
                                                                        </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}